import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { canExposePilotSurface } from "@enrein/shared-contracts/pilot-exposure";
import type { ClientChangeRequestTimelineV1 } from "@enrein/shared-contracts";
import type { PreviewViewV1 } from "@enrein/shared-contracts/approval-change";
import { clientChangePresentation } from "../adapters/change-presentation.js";
import type { GreenfieldClientHubProps } from "../types.js";

export function GreenfieldChangesView({
  changeMessage,
  setChangeMessage,
  active,
  canAct,
  onRequestChange,
  shellStatus,
  api,
  linkId,
  sessionToken,
  deployEnv,
  preview,
  onOpenRevisedPreview,
  safeUpdateSlot,
}: Pick<GreenfieldClientHubProps, "changeMessage" | "setChangeMessage" | "api" | "deployEnv" | "sessionToken"> & {
  readonly active: boolean;
  readonly canAct: boolean;
  readonly onRequestChange: () => void;
  readonly shellStatus: string;
  readonly linkId: string;
  readonly preview: PreviewViewV1 | null;
  readonly onOpenRevisedPreview: (previewVersionId: string) => void;
  readonly safeUpdateSlot?: ReactNode;
}) {
  const canSend = canAct && changeMessage.trim().length > 0;
  const [timeline, setTimeline] = useState<ClientChangeRequestTimelineV1 | null>(null);
  const showSafeEdit =
    shellStatus === "SAFE_EDIT_ACTIVE" &&
    canExposePilotSurface("client.safePatch", "client", {
      deployEnv,
      nodeEnv: import.meta.env.MODE,
      explicitFlags: import.meta.env.VITE_ENREIN_PILOT_FEATURE_FLAGS,
    }).enabled;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void api.listChangeRequests(linkId, sessionToken).then(
      (next) => {
        if (!cancelled) setTimeline(next);
      },
      () => {
        if (!cancelled) setTimeline(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [active, api, linkId, sessionToken, changeMessage]);

  if (!active) return null;

  return (
    <section className="ws-card" id="gf-tab-changes" role="tabpanel" aria-labelledby="gf-tab-btn-changes">
      <h2 className="ws-title">Requested changes</h2>
      {preview?.previewVersionId ? (
        <p className="ws-muted" data-testid="change-request-version-id">
          This change request is for exact preview version {preview.previewVersionId}
        </p>
      ) : null}
      {timeline && timeline.entries.length > 0 ? (
        <ul data-testid="change-request-timeline">
          {timeline.entries.map((entry, index) => {
            const presentation = clientChangePresentation(entry, linkId, preview?.previewVersionId);
            const openPreviewAction =
              presentation?.primaryAction?.intent === "OPEN_CLIENT_REVISED_PREVIEW"
                ? presentation.primaryAction
                : null;
            return (
              <li key={`${entry.createdAt}-${index}`}>
                <p>
                  <strong>{presentation?.title ?? entry.statusLabel}</strong>
                  {presentation?.reason ? ` — ${presentation.reason}` : ` — ${entry.resolutionLabel}`}
                </p>
                <p className="ws-muted">{entry.message}</p>
                {openPreviewAction ? (
                  <button
                    type="button"
                    className="ws-btn ws-btn-primary"
                    data-testid="open-revised-preview"
                    onClick={() => onOpenRevisedPreview(openPreviewAction.previewVersionId)}
                  >
                    {openPreviewAction.label}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="ws-field">
        <label htmlFor="gf-change">Change request</label>
        {!canAct ? <p className="ws-muted">Change requests are closed for the current decision.</p> : null}
        <textarea
          id="gf-change"
          value={changeMessage}
          onChange={(event) => setChangeMessage(event.target.value)}
          placeholder="Describe the change you want reviewed."
          disabled={!canAct}
        />
      </div>
      <button type="button" className="ws-btn ws-btn-primary" disabled={!canSend} onClick={onRequestChange}>
        Send change request
      </button>
      {showSafeEdit ? (
        <div data-testid="client-safe-update">
          <h3 className="ws-title">Safe update</h3>
          {safeUpdateSlot}
        </div>
      ) : null}
    </section>
  );
}
